import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import equipmentData from '../character/data/equipment.json';
import { fuzzyFilter } from '../search/fuzzySearch';
import { EquipmentFeature } from '../components/EquipmentFeature';
import { useIsMobile } from '../hooks/useIsMobile';
import { useNewSourceFilter } from '../hooks/useNewSourceFilter';
import ItemSheet, { ItemLink, ItemLinkTarget, openOnClick } from '../components/ItemSheet';
import { StatFeature, StatItem } from '../components/StatBlock';
import { useSort } from '../hooks/useSort';
import { useStrings } from '../locales';
import { sourceLabel, matchesSource, SOURCE_OPTIONS } from '../character/content/sources';
import { SortIcon, SortState } from '../components/SortIcon';
import './Equipment.css'; // Import the CSS file

/* ── Types ───────────────────────────────────────────────────────── */

interface ColorEntry {
  bg: string;
  color: string;
}

interface Weapon {
  name: string;
  category: string;
  damageType: string;
  tier: number;
  trait: string;
  range: string;
  damage: string;
  burden: string | null;
  feature: string | null;
  isUnique: boolean;
  source?: string;
}

interface Armor {
  name: string;
  tier: number;
  major: number | string;
  severe: number | string;
  score: number | string;
  feature: string | null;
  isUnique: boolean;
  source?: string;
}

interface Wheelchair {
  name: string;
  frame: string;
  damageType: string;
  tier: number;
  trait: string;
  range: string;
  damage: string;
  burden: string;
  feature: string | null;
}

const { weapons, armor, wheelchairs } = equipmentData as { weapons: Weapon[]; armor: Armor[]; wheelchairs: Wheelchair[] };

const TRAIT_COLORS: Record<string, ColorEntry> = {
  Strength: { bg: 'rgba(180,40,40,0.15)', color: '#ff7070' },
  Agility: { bg: 'rgba(39,160,80,0.15)', color: '#60d890' },
  Finesse: { bg: 'rgba(210,30,140,0.15)', color: '#f06ab0' },
  Instinct: { bg: 'rgba(36,100,180,0.15)', color: '#6aaeff' },
  Presence: { bg: 'rgba(120,50,180,0.15)', color: '#c47fff' },
  Knowledge: { bg: 'rgba(201,162,39,0.15)', color: '#e8c547' },
};
const ALL_TRAITS = ['All', ...Object.keys(TRAIT_COLORS)];

const RANGE_ORDER: Record<string, number> = { 'Melee': 0, 'Very Close': 1, 'Close': 2, 'Far': 3, 'Very Far': 4 };

function TraitBadge({ trait }: { trait: string }) {
  const s = TRAIT_COLORS[trait] || { bg: 'var(--bg-card)', color: 'var(--text-muted)' };
  return (
    <span className="trait-badge" style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}55` }}>
      {trait}
    </span>
  );
}

function TierBadge({ tier }: { tier: number }) {
  const colors = ['', '#a89cc8', '#60d890', '#e8c547', '#f06ab0'];
  return (
    <span className="tier-badge" style={{ color: colors[tier] || 'var(--text-muted)', border: `1px solid ${colors[tier] || 'var(--border)'}55` }}>
      T{tier}
    </span>
  );
}


/* ── Mobile card views (also the body of the item sheet) ─────────── */

function WeaponCard({ w, highlight, link }: { w: Weapon; highlight: boolean; link?: ItemLinkTarget }) {
  return (
    <div className={`weapon-card ${highlight ? 'weapon-card-highlight' : 'weapon-card-default'}${link ? ' item-row' : ''}`} onClick={link && openOnClick(link.onOpen)}>
      <div className="weapon-card-header">
        <span className={`weapon-name ${w.isUnique ? 'weapon-name-unique' : 'weapon-name-standard'}`}>{link ? <ItemLink {...link}>{w.name}</ItemLink> : w.name}{w.source && <span className="source-label source-label--inline">{sourceLabel(w.source)}</span>}</span>
        <TierBadge tier={w.tier} />
      </div>
      <div className="weapon-details">
        <TraitBadge trait={w.trait} />
        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{w.category}</span>
        <div className="weapon-detail-divider" />
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{w.range}</span>
        {w.burden && <><div className="weapon-detail-divider" /><span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Burden {w.burden}</span></>}
      </div>
      <div className="weapon-damage">
        <span className={`weapon-damage-value ${w.damageType === 'Magical' ? 'weapon-damage-magical' : 'weapon-damage-physical'}`}>{w.damage}</span>
        <span className="weapon-damage-type">{w.damageType === 'Magical' ? 'mag' : 'phy'}</span>
      </div>
      {w.feature && (
        <div className="weapon-feature">
          <EquipmentFeature feature={w.feature} />
        </div>
      )}
    </div>
  );
}

function ArmorCard({ a, highlight, link }: { a: Armor; highlight: boolean; link?: ItemLinkTarget }) {
  return (
    <div className={`armor-card ${highlight ? 'armor-card-highlight' : 'armor-card-default'}${link ? ' item-row' : ''}`} onClick={link && openOnClick(link.onOpen)}>
      <div className="armor-card-header">
        <span className={`armor-name ${a.isUnique ? 'armor-name-unique' : 'armor-name-standard'}`}>{link ? <ItemLink {...link}>{a.name}</ItemLink> : a.name}{a.source && <span className="source-label source-label--inline">{sourceLabel(a.source)}</span>}</span>
        <TierBadge tier={a.tier} />
      </div>
      <div className="armor-stats">
        <div className="armor-stat">
          <div className="armor-stat-label">major</div>
          <div className="armor-stat-value armor-stat-major">{a.major}</div>
        </div>
        <div className="armor-stat">
          <div className="armor-stat-label">severe</div>
          <div className="armor-stat-value armor-stat-severe">{a.severe}</div>
        </div>
        <div className="armor-stat">
          <div className="armor-stat-label">Score</div>
          <div className="armor-stat-value armor-stat-score">{a.score}</div>
        </div>
      </div>
      {a.feature && (
        <div className="armor-feature">
          <EquipmentFeature feature={a.feature} />
        </div>
      )}
    </div>
  );
}

function FrameCard({ f, highlight, link }: { f: Wheelchair; highlight: boolean; link?: ItemLinkTarget }) {
  return (
    <div className={`weapon-card ${highlight ? 'weapon-card-highlight' : 'weapon-card-default'}${link ? ' item-row' : ''}`} onClick={link && openOnClick(link.onOpen)}>
      <div className="weapon-card-header">
        <span className="weapon-name weapon-name-standard">{link ? <ItemLink {...link}>{f.name}</ItemLink> : f.name}</span>
        <TierBadge tier={f.tier} />
      </div>
      <div className="weapon-details">
        <TraitBadge trait={f.trait} />
        <div className="weapon-detail-divider" />
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{f.range}</span>
        <div className="weapon-detail-divider" />
        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{f.burden}</span>
      </div>
      <div className="weapon-damage">
        <span className={`weapon-damage-value ${f.damageType === 'Magical' ? 'weapon-damage-magical' : 'weapon-damage-physical'}`}>{f.damage}</span>
      </div>
      {f.feature && (
        <div className="weapon-feature">
          <EquipmentFeature feature={f.feature} />
        </div>
      )}
    </div>
  );
}

/* ── Item sheet content (SRD stat card) ──────────────────────────── */
/** Trait, range, damage and burden of a weapon or wheelchair frame. */
function attackStats(w: Weapon | Wheelchair): StatItem[][] {
  return [[
    { label: 'Trait', value: w.trait },
    { label: 'Range', value: w.range },
    { label: 'Damage', value: `${w.damage} ${w.damageType === 'Magical' ? 'mag' : 'phy'}` },
    ...(w.burden ? [{ label: 'Burden', value: w.burden }] : []),
  ]];
}

/** A "Name: text" feature as the SRD card's FEATURE section. */
function FeatureSection({ feature }: { feature?: string | null }) {
  if (!feature) return null;
  const colon = feature.indexOf(':');
  return (
    <>
      <h3 className="srdcard__features">Feature</h3>
      <StatFeature feature={colon > 0 ? { name: feature.slice(0, colon), text: feature.slice(colon + 1).trim() } : { text: feature }} />
    </>
  );
}

function SortTh({ col, sort, onSort, children, style, className }: {
  col: string;
  sort: SortState;
  onSort: (col: string) => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}) {
  return (
    <th
      onClick={() => onSort(col)}
      className={`sortable-th ${className || ''}`}
      style={style}
      aria-sort={sort.col === col ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {children}<SortIcon col={col} sort={sort} />
    </th>
  );
}

/* ── Multi-select filter helpers ────────────────────────────────── */
function toggleFilter(current: Set<string>, value: string): Set<string> {
  if (value === 'All') return new Set(['All']);
  const next = new Set(current);
  next.delete('All');
  if (next.has(value)) {
    next.delete(value);
    if (next.size === 0) next.add('All');
  } else {
    next.add(value);
  }
  return next;
}

function passesFilter(set: Set<string>, value: string): boolean {
  return set.has('All') || set.has(value);
}

/** Source filter: an item passes when any picked source (Core, Hope & Fear, Void) matches it. */
function passesSource(set: Set<string>, source?: string): boolean {
  return set.has('All') || [...set].some((opt) => matchesSource(opt, source));
}

/** Source options a table actually has, so Void only appears once Void items exist. */
const sourceOptions = (items: Array<{ source?: string }>) =>
  SOURCE_OPTIONS.filter((opt) => opt === 'All' || items.some((i) => matchesSource(opt, i.source)));

function VerticalFilter({ label, options, filter, onToggle, colorMap }: {
  label: string;
  options: string[];
  filter: Set<string>;
  onToggle: (next: Set<string>) => void;
  colorMap?: Record<string, ColorEntry>;
}) {
  return (
    <div className="vertical-filter">
      <div className="filter-label vertical-filter-label">{label}</div>
      <div className="vertical-filter-group">
        {options.map((opt) => {
          const active = filter.has(opt);
          const accent = colorMap?.[opt]?.color;
          return (
            <button
              key={opt}
              onClick={() => onToggle(toggleFilter(filter, opt))}
              className={`filter-pill ${active ? 'filter-pill-active' : 'filter-pill-inactive'}`}
              style={active && accent ? { background: `${accent}22`, borderColor: accent, color: accent } : undefined}
              aria-pressed={active}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HorizFilter({ label, options, filter, onToggle, fmt, glows, onPick }: {
  label: string;
  options: string[];
  filter: Set<string>;
  onToggle: (next: Set<string>) => void;
  fmt?: (v: string) => string;
  /** Options to highlight, and a hook called on every pick (the new-source glow) */
  glows?: (v: string) => boolean;
  onPick?: (v: string) => void;
}) {
  return (
    <div className="horizontal-filter">
      <span className="filter-label">{label}</span>
      {options.map((v) => {
        const pressed = filter.has(v);
        return (
          <button
            key={v}
            className={`filter-btn ${pressed ? 'filter-btn-active' : ''}${glows?.(v) ? ' new-source-glow' : ''}`}
            onClick={() => { onToggle(toggleFilter(filter, v)); onPick?.(v); }}
            aria-pressed={pressed}
          >
            {fmt ? fmt(v) : v}
          </button>
        );
      })}
    </div>
  );
}

const TIER_OPTS = ['All', '1', '2', '3', '4'];
const WEAPON_SOURCES = sourceOptions(weapons);
const ARMOR_SOURCES = sourceOptions(armor);
const fmtTier = (v: string): string => v === 'All' ? 'All' : `T${v}`;

/* ── Damage parser: "2d6+4" → { face:6, num:2, mod:4 } ─────────────
   Sort order: die face first (d8 > d6 regardless of modifier),
   then number of dice (2d6 > 1d6), then flat modifier (+7 > +4).   */
function parseDamage(str: string | null | undefined): { face: number; num: number; mod: number } {
  if (!str) return { face: 0, num: 0, mod: 0 };
  const m = (str + '').match(/^(\d*)d(\d+)([+-]\d+)?/i);
  if (!m) return { face: 0, num: 0, mod: 0 };
  return {
    num: parseInt(m[1] || '1', 10),
    face: parseInt(m[2], 10),
    mod: parseInt(m[3] || '0', 10),
  };
}

const ALL = new Set(['All']);
const isDefault = (s: Set<string>): boolean => s.size === 1 && s.has('All');

/* ── Reusable reset button ───────────────────────────────────────── */
function ResetBtn({ dirty, onClick }: { dirty: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={!dirty}
      title="Reset all filters and sorting"
      className={`reset-btn ${dirty ? 'reset-btn-dirty' : ''}`}
    >
      <RotateCcw size={13} /> Reset
    </button>
  );
}

function cmp(a: string | number, b: string | number, dir: string): number {
  if (a < b) return dir === 'asc' ? -1 : 1;
  if (a > b) return dir === 'asc' ? 1 : -1;
  return 0;
}

function sortWeapons(list: Weapon[], sort: SortState): Weapon[] {
  if (!sort.col) return list;
  return [...list].sort((a, b) => {
    const d = sort.dir;
    switch (sort.col) {
      case 'name': return cmp(a.name, b.name, d);
      case 'cat': return cmp(a.category, b.category, d);
      case 'tier': {
        const tierCmp = cmp(a.tier, b.tier, d);
        if (tierCmp !== 0) return tierCmp;
        // Secondary: Physical (0) before Magical (1)
        const dmgA = a.damageType === 'Physical' ? 0 : 1;
        const dmgB = b.damageType === 'Physical' ? 0 : 1;
        if (dmgA !== dmgB) return dmgA - dmgB;
        // Tertiary: alphabetical by name
        return cmp(a.name, b.name, 'asc');
      }
      case 'trait': return cmp(a.trait, b.trait, d);
      case 'range': return cmp(RANGE_ORDER[a.range] ?? 99, RANGE_ORDER[b.range] ?? 99, d);
      case 'damage': {
        const pa = parseDamage(a.damage);
        const pb = parseDamage(b.damage);
        // 1st: die face  (d8 > d6, even if d6+20)
        if (pa.face !== pb.face) return cmp(pa.face, pb.face, d);
        // 2nd: number of dice  (2d6 > 1d6)
        if (pa.num !== pb.num) return cmp(pa.num, pb.num, d);
        // 3rd: flat modifier  (d6+7 > d6+4)
        return cmp(pa.mod, pb.mod, d);
      }
      case 'burden': return cmp(a.burden ?? '', b.burden ?? '', d);
      case 'feature': return cmp(a.feature ?? '', b.feature ?? '', d);
      default: return 0;
    }
  });
}

function sortArmor(list: Armor[], sort: SortState): Armor[] {
  if (!sort.col) return list;
  return [...list].sort((a, b) => {
    const d = sort.dir;
    switch (sort.col) {
      case 'name': return cmp(a.name, b.name, d);
      case 'tier': return cmp(a.tier, b.tier, d);
      case 'major': return cmp(Number(a.major) || 0, Number(b.major) || 0, d);
      case 'severe': return cmp(Number(a.severe) || 0, Number(b.severe) || 0, d);
      case 'score': return cmp(Number(a.score) || 0, Number(b.score) || 0, d);
      case 'feature': return cmp(a.feature ?? '', b.feature ?? '', d);
      default: return 0;
    }
  });
}

/* ════════════════════════════════════════════════════════════════
   Main page
   ════════════════════════════════════════════════════════════════ */
export default function Equipment() {
  const s = useStrings().equipment;
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const rawTab = searchParams.get('tab');
  const initTab = rawTab === 'armor' ? 'armor' : rawTab === 'frames' ? 'frames' : 'weapons';
  const initQ = searchParams.get('q') || '';

  /* Back-to-class breadcrumb, set by EquipLink in Classes.js */
  const backTo = location.state?.backTo;
  const backLabel = location.state?.backLabel;

  const isMobile = useIsMobile();
  const [showFilters, setShowFilters] = useState(false);

  const [tab, setTab] = useState(initTab);

  /* Item sheet: an item's link (?tab=...&item=Name) opens its shareable sheet */
  const [openItem, setOpenItem] = useState(() => searchParams.get('item'));
  const itemHref = (t: string, name: string) => `/equipment?tab=${t}&item=${encodeURIComponent(name)}`;
  const linkTo = (t: string, name: string): ItemLinkTarget => ({
    href: itemHref(t, name),
    onOpen: () => { setOpenItem(name); setSearchParams({ tab: t, item: name }, { replace: true }); },
  });
  const closeItem = useCallback(() => { setOpenItem(null); setSearchParams({ tab }, { replace: true }); }, [tab, setSearchParams]);
  const openWeapon = tab === 'weapons' ? weapons.find((w) => w.name === openItem) : undefined;
  const openArmor = tab === 'armor' ? armor.find((a) => a.name === openItem) : undefined;
  const openFrame = tab === 'frames' ? wheelchairs.find((f) => f.name === openItem) : undefined;
  const sheet = openWeapon ? { name: openWeapon.name, kind: `Tier ${openWeapon.tier} ${openWeapon.category} Weapon`, stats: attackStats(openWeapon), source: openWeapon.source, feature: openWeapon.feature }
    : openArmor ? { name: openArmor.name, kind: `Tier ${openArmor.tier} Armor`, stats: [[{ label: 'Thresholds', value: `${openArmor.major}/${openArmor.severe}` }, { label: 'Base Score', value: openArmor.score }]], source: openArmor.source, feature: openArmor.feature }
    : openFrame ? { name: openFrame.name, kind: `Tier ${openFrame.tier} Wheelchair`, stats: attackStats(openFrame), source: undefined, feature: openFrame.feature }
    : null;
  const [wSearch, setWSearch] = useState(initTab === 'weapons' ? initQ : '');
  const [aSearch, setASearch] = useState(initTab === 'armor' ? initQ : '');
  const [fSearch, setFSearch] = useState(initTab === 'frames' ? initQ : '');

  /* Weapon filters */
  const [catFilter, setCatFilter] = useState(new Set(['All']));
  const [dmgFilter, setDmgFilter] = useState(new Set(['All']));
  const [tierFilter, setTierFilter] = useState(new Set(['All']));
  const [rangeFilter, setRangeFilter] = useState(new Set(['All']));
  const [traitFilter, setTraitFilter] = useState(new Set(['All']));
  const [uniqueFilter, setUniqueFilter] = useState(new Set(['All']));
  const [sourceFilter, setSourceFilter] = useState(new Set(['All']));

  /* Armor filters */
  const [armorTierFilter, setArmorTierFilter] = useState(new Set(['All']));
  const [armorUniqueFilter, setArmorUniqueFilter] = useState(new Set(['All']));
  const [armorSourceFilter, setArmorSourceFilter] = useState(new Set(['All']));
  const { glows, onSourceClick } = useNewSourceFilter('/equipment');

  /* Frame filters */
  const [frameTierFilter, setFrameTierFilter] = useState(new Set(['All']));

  /* Sorting */
  const [wSort, handleWSort, resetWSort] = useSort({ col: 'tier', dir: 'asc' });
  const [aSort, handleASort, resetASort] = useSort({ col: 'tier', dir: 'asc' });
  const [fSort, handleFSort, resetFSort] = useSort({ col: 'tier', dir: 'asc' });

  /* Reset helpers */
  const resetWeapons = () => {
    setWSearch(''); setCatFilter(new Set(ALL)); setDmgFilter(new Set(ALL));
    setTierFilter(new Set(ALL)); setRangeFilter(new Set(ALL));
    setTraitFilter(new Set(ALL)); setUniqueFilter(new Set(ALL)); setSourceFilter(new Set(ALL));
    resetWSort();
  };
  const resetArmor = () => {
    setASearch(''); setArmorTierFilter(new Set(ALL)); setArmorUniqueFilter(new Set(ALL)); setArmorSourceFilter(new Set(ALL));
    resetASort();
  };
  const resetFrames = () => {
    setFSearch(''); setFrameTierFilter(new Set(ALL));
    resetFSort();
  };

  /* Dirty flags, true when anything differs from defaults */
  const wDirty = !!wSearch || !isDefault(catFilter) || !isDefault(dmgFilter) ||
    !isDefault(tierFilter) || !isDefault(rangeFilter) || !isDefault(traitFilter) ||
    !isDefault(uniqueFilter) || !isDefault(sourceFilter) || wSort.col !== 'tier' || wSort.dir !== 'asc';
  const aDirty = !!aSearch || !isDefault(armorTierFilter) || !isDefault(armorUniqueFilter) || !isDefault(armorSourceFilter) ||
    aSort.col !== 'tier' || aSort.dir !== 'asc';
  const fDirty = !!fSearch || !isDefault(frameTierFilter) || fSort.col !== 'tier' || fSort.dir !== 'asc';

  const filteredWeapons = useMemo(() => {
    const base = weapons.filter((w) => {
      if (!passesFilter(catFilter, w.category)) return false;
      if (!passesFilter(dmgFilter, w.damageType)) return false;
      if (!passesFilter(tierFilter, String(w.tier))) return false;
      if (!passesFilter(rangeFilter, w.range)) return false;
      if (!passesFilter(traitFilter, w.trait)) return false;
      if (!passesSource(sourceFilter, w.source)) return false;
      if (!uniqueFilter.has('All')) {
        const wantStd = uniqueFilter.has('Standard');
        const wantNmd = uniqueFilter.has('Named');
        if (wantStd && !wantNmd && w.isUnique) return false;
        if (!wantStd && wantNmd && !w.isUnique) return false;
      }
      return true;
    });
    const searched = fuzzyFilter(
      base, wSearch,
      (w) => [w.trait, w.feature || '', w.range, w.category].join(' '),
      (w) => w.name
    );
    // Preserve fuzzy relevance order during search so equally-scoring items
    // (e.g. all "Revolver" variants) stay together at the top.
    if (wSearch.trim()) return searched;
    return sortWeapons(searched, wSort);
  }, [catFilter, dmgFilter, tierFilter, rangeFilter, traitFilter, uniqueFilter, sourceFilter, wSearch, wSort]);

  const filteredArmor = useMemo(() => {
    const base = armor.filter((a) => {
      if (!passesFilter(armorTierFilter, String(a.tier))) return false;
      if (!passesSource(armorSourceFilter, a.source)) return false;
      if (!armorUniqueFilter.has('All')) {
        const wantStd = armorUniqueFilter.has('Standard');
        const wantNmd = armorUniqueFilter.has('Named');
        if (wantStd && !wantNmd && a.isUnique) return false;
        if (!wantStd && wantNmd && !a.isUnique) return false;
      }
      return true;
    });
    const searched = fuzzyFilter(
      base, aSearch,
      (a) => a.feature || '',
      (a) => a.name
    );
    // Preserve fuzzy relevance order during search.
    if (aSearch.trim()) return searched;
    return sortArmor(searched, aSort);
  }, [armorTierFilter, armorUniqueFilter, armorSourceFilter, aSearch, aSort]);

  const filteredFrames = useMemo(() => {
    const base = wheelchairs.filter((f) => {
      if (!passesFilter(frameTierFilter, String(f.tier))) return false;
      return true;
    });
    const searched = fuzzyFilter(
      base, fSearch,
      (f) => [f.frame, f.trait, f.feature || ''].join(' '),
      (f) => f.name
    );
    if (fSearch.trim()) return searched;
    return [...searched].sort((a, b) => {
      const dir = fSort.dir === 'asc' ? 1 : -1;
      if (fSort.col === 'tier') return (a.tier - b.tier) * dir;
      if (fSort.col === 'name') return a.name.localeCompare(b.name) * dir;
      if (fSort.col === 'damage') return a.damage.localeCompare(b.damage) * dir;
      return 0;
    });
  }, [frameTierFilter, fSearch, fSort]);

  return (
    <div className="page-content">
      {backTo && (
        <button
          onClick={() => navigate(backTo)}
          className="back-btn"
        >
          {s.backTo(backLabel)}
        </button>
      )}

      <div className="page-header">
        <h1>{s.title}</h1>
        <p>{s.desc(weapons.length, armor.length)}</p>

      </div>

      {/* Tab bar */}
      <div className="tab-bar" role="tablist">
        {[
          { id: 'weapons', label: s.tabs.weapons(weapons.length) },
          { id: 'armor', label: s.tabs.armor(armor.length) },
          { id: 'frames', label: s.tabs.frames(wheelchairs.length) },
        ].map((t, i, arr) => {
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              id={`tab-${t.id}`}
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              className={`tab-btn ${isActive ? 'tab-btn-active' : ''}`}
              style={{ borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ════════════════ WEAPONS ════════════════ */}
      {tab === 'weapons' && (
        <div role="tabpanel" aria-labelledby="tab-weapons">
          <input
            type="text"
            id="weapons-search"
            name="equipment-weapons-search"
            placeholder={s.weapons.searchPlaceholder}
            value={wSearch}
            onChange={(e) => setWSearch(e.target.value)}
            className="search-input filter-search-bar"
            aria-label={s.weapons.searchAria}
          />
          <div className="filter-layout">

            {/* Mobile filter toggle row */}
            <div className="filter-toggle-row">
              <button className="filter-toggle-btn" onClick={() => setShowFilters(f => !f)}>
                <span>⚙ Filters{wDirty ? ' •' : ''}</span>
                <span>{showFilters ? '▲' : '▼'}</span>
              </button>
              <ResetBtn dirty={wDirty} onClick={resetWeapons} />
            </div>

            {/* Mobile active filter chips */}
            {(() => {
              const TC: Record<string, string> = { '1': '#a89cc8', '2': '#60d890', '3': '#e8c547', '4': '#f06ab0' };
              const DMG: Record<string, string> = { Physical: '#e8c547', Magical: '#c47fff' };
              const chips = [
                ...(wSearch ? [{ label: `"${wSearch}"`, color: 'var(--gold)', onRemove: () => setWSearch('') }] : []),
                ...[...catFilter].filter(v => v !== 'All').map(v => ({ label: v, color: 'var(--gold)', onRemove: () => setCatFilter(p => toggleFilter(p, v)) })),
                ...[...dmgFilter].filter(v => v !== 'All').map(v => ({ label: v, color: DMG[v], onRemove: () => setDmgFilter(p => toggleFilter(p, v)) })),
                ...[...tierFilter].filter(v => v !== 'All').map(v => ({ label: `T${v}`, color: TC[v], onRemove: () => setTierFilter(p => toggleFilter(p, v)) })),
                ...[...rangeFilter].filter(v => v !== 'All').map(v => ({ label: v, color: 'var(--gold)', onRemove: () => setRangeFilter(p => toggleFilter(p, v)) })),
                ...[...traitFilter].filter(v => v !== 'All').map(v => ({ label: v, color: TRAIT_COLORS[v]?.color, onRemove: () => setTraitFilter(p => toggleFilter(p, v)) })),
                ...[...uniqueFilter].filter(v => v !== 'All').map(v => ({ label: v, color: 'var(--gold)', onRemove: () => setUniqueFilter(p => toggleFilter(p, v)) })),
                ...[...sourceFilter].filter(v => v !== 'All').map(v => ({ label: v, color: 'var(--gold)', onRemove: () => setSourceFilter(p => toggleFilter(p, v)) })),
              ];
              return chips.length > 0 ? (
                <div className="mobile-filter-chips">
                  {chips.map(({ label, color, onRemove }) => {
                    const c = color || 'var(--gold)';
                    return (
                      <span key={label} className="filter-chip" style={{ background: `${c}18`, border: `1px solid ${c}44`, color: c }}>
                        {label}
                        <button onClick={onRemove} className="filter-chip-remove" style={{ color: c }}>×</button>
                      </span>
                    );
                  })}
                </div>
              ) : null;
            })()}

            {/* Left panel */}
            <div className={`filter-sidebar${showFilters ? '' : ' filter-sidebar--hidden'}`}>
              {/* Header row: label + reset */}
              <div className="filter-sidebar-header">
                <span className="filter-label">Filters</span>
                <span className="hide-mobile"><ResetBtn dirty={wDirty} onClick={resetWeapons} /></span>
              </div>
              <div className="filter-count">{s.weapons.countLabel(filteredWeapons.length, weapons.length)}</div>
              <VerticalFilter label="Category" options={['All', 'Primary', 'Secondary']} filter={catFilter} onToggle={setCatFilter} />
              <VerticalFilter label="Trait" options={ALL_TRAITS} filter={traitFilter} onToggle={setTraitFilter} colorMap={TRAIT_COLORS} />
              <VerticalFilter label="Type" options={['All', 'Standard', 'Named']} filter={uniqueFilter} onToggle={setUniqueFilter} />
            </div>

            {/* Right column */}
            <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>

              {/* Horizontal filter bar */}
              <div className="filter-bar">
                <HorizFilter label="Damage" options={['All', 'Physical', 'Magical']} filter={dmgFilter} onToggle={setDmgFilter} />
                <div className="filter-divider" />
                <HorizFilter label="Range" options={['All', 'Melee', 'Very Close', 'Close', 'Far', 'Very Far']} filter={rangeFilter} onToggle={setRangeFilter} />
                <div className="filter-divider" />
                <HorizFilter label="Tier" options={TIER_OPTS} filter={tierFilter} onToggle={setTierFilter} fmt={fmtTier} />
                <div className="filter-divider" />
                <HorizFilter label="Source" options={WEAPON_SOURCES} filter={sourceFilter} onToggle={setSourceFilter} glows={glows} onPick={onSourceClick} />
              </div>

              {filteredWeapons.length === 0 && (
                <div className="empty-state"><p>{s.weapons.noMatch}</p></div>
              )}

              {/* ── Weapon row helper ── */}
              {(() => {
                const isGrouped = !wSearch.trim();
                const COLS = 7;

                const CAT_BORDER: Record<string, string> = {
                  Primary: 'rgba(201,162,39,0.55)',
                  Secondary: 'rgba(100,150,220,0.55)',
                };

                const WeaponRow = ({ w, highlight }: { w: Weapon; highlight: boolean }) => (
                  <tr className={`item-row${highlight ? ' tr-highlight' : ''}`} onClick={openOnClick(linkTo('weapons', w.name).onOpen)}>
                    <td style={{
                      color: w.isUnique ? 'var(--gold)' : 'var(--text-primary)',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      boxShadow: `inset 3px 0 0 ${CAT_BORDER[w.category] ?? 'transparent'}`,
                      paddingLeft: '10px',
                    }}><ItemLink {...linkTo('weapons', w.name)}>{w.name}</ItemLink>{w.source && <span className="source-label source-label--inline">{sourceLabel(w.source)}</span>}</td>
                    <td><TierBadge tier={w.tier} /></td>
                    <td><TraitBadge trait={w.trait} /></td>
                    <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{w.range}</td>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700, color: w.damageType === 'Magical' ? '#c47fff' : 'var(--gold)', fontSize: '13px' }}>{w.damage}</span>
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '4px' }}>{w.damageType === 'Magical' ? 'mag' : 'phy'}</span>
                    </td>
                    <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{w.burden}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-secondary)', maxWidth: '280px' }}>
                      {w.feature ? <EquipmentFeature feature={w.feature} /> : '-'}
                    </td>
                  </tr>
                );

                if (isMobile) {
                  if (!isGrouped) {
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {filteredWeapons.map((w, idx) => (
                          <WeaponCard key={w.name + w.tier} w={w} highlight={idx === 0} link={linkTo('weapons', w.name)} />
                        ))}
                      </div>
                    );
                  }
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {(['Primary', 'Secondary'] as const).map(cat => {
                        const catWeapons = sortWeapons(filteredWeapons.filter(w => w.category === cat), wSort);
                        if (catWeapons.length === 0) return null;
                        return (
                          <div key={cat}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderBottom: `1px solid ${CAT_BORDER[cat]}55`, paddingBottom: '6px', marginBottom: '8px' }}>
                              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: CAT_BORDER[cat], flexShrink: 0 }} />
                              <span style={{ fontFamily: 'var(--font-display)', fontSize: '13px', fontWeight: 700, color: 'var(--gold)' }}>{cat}</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {catWeapons.map(w => (
                                <div key={w.name + w.tier} style={{ boxShadow: `inset 3px 0 0 ${CAT_BORDER[w.category] ?? 'transparent'}`, paddingLeft: '8px' }}>
                                  <WeaponCard w={w} highlight={false} link={linkTo('weapons', w.name)} />
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                return (
                  <div className="equipment-table-wrap">
                    <table className="equipment-table">
                      <colgroup>
                        <col className="equipment-col--name" />
                        <col className="equipment-col--t" />
                        <col className="equipment-col--trait" />
                        <col className="equipment-col--range" />
                        <col className="equipment-col--damage" />
                        <col className="equipment-col--burden" />
                        <col className="equipment-col--feature" />
                      </colgroup>
                      <thead>
                        <tr>
                          <SortTh col="name" sort={wSort} onSort={handleWSort}>{s.weapons.tableHeaders.name}</SortTh>
                          <SortTh col="tier" sort={wSort} onSort={handleWSort}>{s.weapons.tableHeaders.tier}</SortTh>
                          <SortTh col="trait" sort={wSort} onSort={handleWSort}>{s.weapons.tableHeaders.trait}</SortTh>
                          <SortTh col="range" sort={wSort} onSort={handleWSort}>{s.weapons.tableHeaders.range}</SortTh>
                          <SortTh col="damage" sort={wSort} onSort={handleWSort}>{s.weapons.tableHeaders.damage}</SortTh>
                          <SortTh col="burden" sort={wSort} onSort={handleWSort}>{s.weapons.tableHeaders.burden}</SortTh>
                          <SortTh col="feature" sort={wSort} onSort={handleWSort}>{s.weapons.tableHeaders.feature}</SortTh>
                        </tr>
                      </thead>
                      <tbody>
                        {isGrouped ? (
                          (['Primary', 'Secondary'] as const).flatMap(cat => {
                            const catWeapons = sortWeapons(filteredWeapons.filter(w => w.category === cat), wSort);
                            if (catWeapons.length === 0) return [];
                            return [
                              <tr key={`sec-${cat}`} className="weapon-section-header">
                                <td colSpan={COLS}>
                                  <span className="weapon-section-label">
                                    <span className="weapon-section-dot" style={{ background: CAT_BORDER[cat] }} />
                                    {cat}
                                  </span>
                                </td>
                              </tr>,
                              ...catWeapons.map(w => <WeaponRow key={w.name + w.tier} w={w} highlight={false} />),
                            ];
                          })
                        ) : (
                          filteredWeapons.map((w, idx) => (
                            <WeaponRow key={w.name + w.tier} w={w} highlight={idx === 0} />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              <div className="info-box">
                <h3 className="info-box-title">Common Weapon Properties</h3>
                <div className="info-grid">
                  {[
                    { k: 'Reliable', v: '+1 to attack rolls.' },
                    { k: 'Quick', v: 'Mark a Stress to target an additional creature within range.' },
                    { k: 'Powerful', v: 'On success, roll an extra damage die and discard the lowest.' },
                    { k: 'Massive', v: '-1 to Evasion; on success, roll an extra damage die and discard the lowest.' },
                    { k: 'Heavy', v: '-1 to Evasion while equipped.' },
                    { k: 'Cumbersome', v: '-1 to Finesse.' },
                    { k: 'Paired', v: 'Grants a bonus to primary weapon damage when both are equipped.' },
                    { k: 'Protective', v: 'Adds to Armor Score when equipped.' },
                    { k: 'Barrier', v: 'Higher bonus to Armor Score; -1 to Evasion.' },
                    { k: 'Returning', v: 'Returns to your hand after being thrown.' },
                    { k: 'Brutal', v: 'Roll an additional damage die when you roll the maximum on any die.' },
                    { k: 'Versatile', v: 'Can be used in an alternate mode with different stats.' },
                  ].map(({ k, v }) => (
                    <div key={k} className="info-item">
                      <strong>{k}:</strong> {v}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════ ARMOR ════════════════ */}
      {tab === 'armor' && (
        <div role="tabpanel" aria-labelledby="tab-armor">
          <input
            type="text"
            id="armor-search"
            name="equipment-armor-search"
            placeholder={s.armor.searchPlaceholder}
            value={aSearch}
            onChange={(e) => setASearch(e.target.value)}
            className="search-input filter-search-bar"
            aria-label={s.armor.searchAria}
          />
          <div className="filter-layout">

            {/* Mobile filter toggle row */}
            <div className="filter-toggle-row">
              <button className="filter-toggle-btn" onClick={() => setShowFilters(f => !f)}>
                <span>⚙ Filters{aDirty ? ' •' : ''}</span>
                <span>{showFilters ? '▲' : '▼'}</span>
              </button>
              <ResetBtn dirty={aDirty} onClick={resetArmor} />
            </div>

            {/* Mobile active filter chips */}
            {(() => {
              const TC: Record<string, string> = { '1': '#a89cc8', '2': '#60d890', '3': '#e8c547', '4': '#f06ab0' };
              const chips = [
                ...(aSearch ? [{ label: `"${aSearch}"`, color: 'var(--gold)', onRemove: () => setASearch('') }] : []),
                ...[...armorTierFilter].filter(v => v !== 'All').map(v => ({ label: `T${v}`, color: TC[v], onRemove: () => setArmorTierFilter(p => toggleFilter(p, v)) })),
                ...[...armorUniqueFilter].filter(v => v !== 'All').map(v => ({ label: v, color: 'var(--gold)', onRemove: () => setArmorUniqueFilter(p => toggleFilter(p, v)) })),
                ...[...armorSourceFilter].filter(v => v !== 'All').map(v => ({ label: v, color: 'var(--gold)', onRemove: () => setArmorSourceFilter(p => toggleFilter(p, v)) })),
              ];
              return chips.length > 0 ? (
                <div className="mobile-filter-chips">
                  {chips.map(({ label, color, onRemove }) => {
                    const c = color || 'var(--gold)';
                    return (
                      <span key={label} className="filter-chip" style={{ background: `${c}18`, border: `1px solid ${c}44`, color: c }}>
                        {label}
                        <button onClick={onRemove} className="filter-chip-remove" style={{ color: c }}>×</button>
                      </span>
                    );
                  })}
                </div>
              ) : null;
            })()}

            {/* Left panel */}
            <div className={`filter-sidebar${showFilters ? '' : ' filter-sidebar--hidden'}`}>
              {/* Header row: label + reset */}
              <div className="filter-sidebar-header">
                <span className="filter-label">Filters</span>
                <span className="hide-mobile"><ResetBtn dirty={aDirty} onClick={resetArmor} /></span>
              </div>
              <div className="filter-count">{filteredArmor.length} of {armor.length} armor</div>
              <VerticalFilter label="Type" options={['All', 'Standard', 'Named']} filter={armorUniqueFilter} onToggle={setArmorUniqueFilter} />
            </div>

            {/* Right column */}
            <div style={{ flex: 1, minWidth: 0 }}>

              {/* Horizontal filter bar */}
              <div className="filter-bar">
                <HorizFilter label="Tier" options={TIER_OPTS} filter={armorTierFilter} onToggle={setArmorTierFilter} fmt={fmtTier} />
                <div className="filter-divider" />
                <HorizFilter label="Source" options={ARMOR_SOURCES} filter={armorSourceFilter} onToggle={setArmorSourceFilter} glows={glows} onPick={onSourceClick} />
              </div>

              {filteredArmor.length === 0 && (
                <div className="empty-state"><p>{s.armor.noMatch}</p></div>
              )}

              {isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {filteredArmor.map((a, idx) => (
                    <ArmorCard key={a.name} a={a} highlight={idx === 0 && !!aSearch} link={linkTo('armor', a.name)} />
                  ))}
                </div>
              ) : (
                <div className="equipment-table-wrap">
                  <table className="equipment-table">
                    <colgroup>
                      <col className="equipment-col--armor-name" />
                      <col className="equipment-col--armor-tier" />
                      <col className="equipment-col--armor-stat" />
                      <col className="equipment-col--armor-stat" />
                      <col className="equipment-col--armor-stat" />
                      <col className="equipment-col--armor-feature" />
                    </colgroup>
                    <thead>
                      <tr>
                        <SortTh col="name" sort={aSort} onSort={handleASort}>{s.armor.tableHeaders.name}</SortTh>
                        <SortTh col="tier" sort={aSort} onSort={handleASort}>{s.armor.tableHeaders.tier}</SortTh>
                        <SortTh col="major" sort={aSort} onSort={handleASort}>{s.armor.tableHeaders.major}</SortTh>
                        <SortTh col="severe" sort={aSort} onSort={handleASort}>{s.armor.tableHeaders.severe}</SortTh>
                        <SortTh col="score" sort={aSort} onSort={handleASort}>{s.armor.tableHeaders.score}</SortTh>
                        <SortTh col="feature" sort={aSort} onSort={handleASort}>{s.armor.tableHeaders.feature}</SortTh>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredArmor.map((a, idx) => (
                        <tr key={a.name} className={`item-row${idx === 0 && aSearch ? ' tr-highlight' : ''}`} onClick={openOnClick(linkTo('armor', a.name).onOpen)}>
                          <td style={{ color: a.isUnique ? 'var(--gold)' : 'var(--text-primary)', fontWeight: 500, whiteSpace: 'nowrap' }}><ItemLink {...linkTo('armor', a.name)}>{a.name}</ItemLink>{a.source && <span className="source-label source-label--inline">{sourceLabel(a.source)}</span>}</td>
                          <td><TierBadge tier={a.tier} /></td>
                          <td style={{ fontFamily: 'monospace', color: 'var(--gold)', fontWeight: 700 }}>{a.major}</td>
                          <td style={{ fontFamily: 'monospace', color: 'var(--fear)', fontWeight: 700 }}>{a.severe}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--text-primary)' }}>{a.score}</td>
                          <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {a.feature
                              ? <EquipmentFeature feature={a.feature} />
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="info-box">
                <h3 className="info-box-title">Armor Rules</h3>
                <div className="info-grid">
                  {[
                    { k: 'Minor Threshold', v: 'If damage less than the Major value, mark 1 Hit Point.' },
                    { k: 'Major Threshold', v: 'If damage equals or exceeds this value, mark 2 Hit Points.' },
                    { k: 'Severe Threshold', v: 'If damage equals or exceeds this value, mark 3 Hit Points.' },
                    { k: 'Armor Slots', v: 'Mark a slot to reduce incoming damage severity by one threshold. Score = number of slots.' },
                    { k: 'Clearing Slots', v: 'Short rest or long rest clears all marked Armor Slots.' },
                    { k: 'Named Armor', v: 'Shown in gold - these are unique items with special properties found as loot.' },
                  ].map(({ k, v }) => (
                    <div key={k} className="info-item">
                      <strong>{k}:</strong> {v}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════ FRAMES ════════════════ */}
      {tab === 'frames' && (
        <div role="tabpanel" aria-labelledby="tab-frames">
          <input
            type="text"
            id="frames-search"
            name="equipment-frames-search"
            placeholder={s.frames.searchPlaceholder}
            value={fSearch}
            onChange={(e) => setFSearch(e.target.value)}
            className="search-input filter-search-bar"
            aria-label={s.frames.searchAria}
          />
          <div className="filter-layout">

            {/* Mobile filter toggle row */}
            <div className="filter-toggle-row">
              <button className="filter-toggle-btn" onClick={() => setShowFilters(f => !f)}>
                <span>⚙ Filters{fDirty ? ' •' : ''}</span>
                <span>{showFilters ? '▲' : '▼'}</span>
              </button>
              <ResetBtn dirty={fDirty} onClick={resetFrames} />
            </div>

            {/* Left panel */}
            <div className={`filter-sidebar${showFilters ? '' : ' filter-sidebar--hidden'}`}>
              <div className="filter-sidebar-header">
                <span className="filter-label">Filters</span>
                <span className="hide-mobile"><ResetBtn dirty={fDirty} onClick={resetFrames} /></span>
              </div>
              <div className="filter-count">{filteredFrames.length} of {wheelchairs.length} frames</div>
            </div>

            {/* Right column */}
            <div style={{ flex: 1, minWidth: 0 }}>

              {/* Horizontal filter bar */}
              <div className="filter-bar">
                <HorizFilter label="Tier" options={TIER_OPTS} filter={frameTierFilter} onToggle={setFrameTierFilter} fmt={fmtTier} />
              </div>

              {filteredFrames.length === 0 && (
                <div className="empty-state"><p>{s.frames.noMatch}</p></div>
              )}

              {isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {filteredFrames.map((f, idx) => (
                    <FrameCard key={f.name} f={f} highlight={idx === 0 && !!fSearch} link={linkTo('frames', f.name)} />
                  ))}
                </div>
              ) : (
                <div className="equipment-table-wrap">
                  <table className="equipment-table">
                    <colgroup>
                      <col className="equipment-col--name" />
                      <col className="equipment-col--tier" />
                      <col className="equipment-col--trait" />
                      <col className="equipment-col--range" />
                      <col className="equipment-col--damage" />
                      <col className="equipment-col--burden" />
                      <col className="equipment-col--feature" />
                    </colgroup>
                    <thead>
                      <tr>
                        <SortTh col="name" sort={fSort} onSort={handleFSort}>{s.frames.tableHeaders.name}</SortTh>
                        <SortTh col="tier" sort={fSort} onSort={handleFSort}>{s.frames.tableHeaders.tier}</SortTh>
                        <th>{s.frames.tableHeaders.trait}</th>
                        <th>{s.frames.tableHeaders.range}</th>
                        <SortTh col="damage" sort={fSort} onSort={handleFSort}>{s.frames.tableHeaders.damage}</SortTh>
                        <th>{s.frames.tableHeaders.burden}</th>
                        <th>{s.frames.tableHeaders.feature}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(['Light-Frame', 'Heavy-Frame', 'Arcane-Frame'] as const).flatMap(frameType => {
                        const FRAME_COLORS: Record<string, string> = {
                          'Light-Frame': '#60d890',
                          'Heavy-Frame': '#ff7070',
                          'Arcane-Frame': 'var(--hope)',
                        };
                        const group = filteredFrames.filter(f => f.frame === frameType);
                        if (group.length === 0) return [];
                        return [
                          <tr key={`sec-${frameType}`} className="weapon-section-header">
                            <td colSpan={7}>
                              <span className="weapon-section-label">
                                <span className="weapon-section-dot" style={{ background: FRAME_COLORS[frameType] }} />
                                {frameType}
                              </span>
                            </td>
                          </tr>,
                          ...group.map(f => (
                            <tr key={f.name} className="item-row" onClick={openOnClick(linkTo('frames', f.name).onOpen)}>
                              <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}><ItemLink {...linkTo('frames', f.name)}>{f.name}</ItemLink></td>
                              <td><TierBadge tier={f.tier} /></td>
                              <td><TraitBadge trait={f.trait} /></td>
                              <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{f.range}</td>
                              <td style={{ fontFamily: 'monospace', color: f.damageType === 'Magical' ? 'var(--hope)' : 'var(--gold)', fontWeight: 700 }}>{f.damage}</td>
                              <td style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{f.burden}</td>
                              <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                {f.feature ? <EquipmentFeature feature={f.feature} /> : '-'}
                              </td>
                            </tr>
                          )),
                        ];
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="info-box">
                <h3 className="info-box-title">Combat Wheelchair Rules</h3>
                <div className="info-grid">
                  {[
                    { k: 'Light-Frame', v: 'Agility-based. Melee range. Quick feature lets you mark Stress to hit a second target.' },
                    { k: 'Heavy-Frame', v: 'Strength-based. Melee range. Two-Handed. Trades -1 Evasion for heavy damage.' },
                    { k: 'Arcane-Frame', v: 'Uses your subclass Spellcast trait. Far range. One-Handed. +1 to attack rolls.' },
                    { k: 'Burden', v: 'One-Handed frames leave a hand free. Two-Handed frames occupy both hands.' },
                    { k: 'Movement', v: 'Describe movement narratively - rolling, spinning, ramping. Treat as normal character movement.' },
                  ].map(({ k, v }) => (
                    <div key={k} className="info-item">
                      <strong>{k}:</strong> {v}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {sheet && (
        <ItemSheet title={sheet.name} kind={sheet.kind} stats={sheet.stats} source={sheet.source ? sourceLabel(sheet.source) : undefined} href={itemHref(tab, sheet.name)} onClose={closeItem}>
          <FeatureSection feature={sheet.feature} />
        </ItemSheet>
      )}
    </div>
  );
}